import { useMemo } from "react";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  toCreateExpenseReq,
  toUpdateExpenseReq,
  type ExpenseFormData,
} from "src/api/adapters/expense";
import {
  notEmpty,
  validate,
  test,
  type ValidationMapperFunc,
  commaSeparatedDigits,
  greaterThanMinAmount,
  validAges,
  validDateRange,
  lessThanMaxAmount,
  isPastOrCurrentDate,
} from "src/utils";
import {
  ExpenseFields,
  FormTable,
  FormTableBody,
  MoneySecurityIcon,
} from "@porchpass/ui";
import { DomainExpenseType } from "@porchpass/api-client";
import {
  expenseErrorsAfterSubmit,
  fromUiExpensePatch,
  toUiExpense,
} from "src/forms/adapters/expense";

const validationMapper: ValidationMapperFunc<ExpenseFormData> = (data) => ({
  amount: [
    test(notEmpty, "Must enter an amount"),
    test(greaterThanMinAmount(0), "Must be greater than 0"),
    test(lessThanMaxAmount(100_000_000), "Must be less than $100M"),
  ],
  from_month: [test(notEmpty, "Must select a month")],
  from_year: [
    test(notEmpty, "Must select a year"),
    test(
      () => isPastOrCurrentDate(data.from_month ?? "", data.from_year ?? ""),
      "Start date cannot be in the future",
    ),
  ],
  to_year: [
    test(
      () =>
        validDateRange(
          data.from_month ?? "",
          data.from_year ?? "",
          data.to_month ?? "",
          data.to_year ?? "",
        ),
      "End date must be after start date",
    ),
    test(
      () =>
        !(
          !!data.to_month &&
          !!data.to_year &&
          isPastOrCurrentDate(data.to_month ?? "", data.to_year ?? "")
        ),
      "Please select a future date",
    ),
  ],
  children_ages: [
    test(validAges, "Children's ages must be a valid age"),
    test(commaSeparatedDigits, "Children's ages must be comma separated"),
  ],
});

type ChildSupportModalProps = {
  expense: Partial<ExpenseFormData>;
  closeHandler: () => void;
  confirmHandler: () => void;
};

export default ({
  expense,
  closeHandler,
  confirmHandler,
}: ChildSupportModalProps) => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<ExpenseFormData>(
    { ...expense },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );
  const [submitHandler, loading, , hasAttemptedFormSubmission] =
    useDlaFormSubmit(
      async (data: ExpenseFormData) => {
        if (expense.id) {
          return api.updateExpense(expense.id, toUpdateExpenseReq(data));
        }
        return api.createExpense(toCreateExpenseReq(data));
      },
      { onSuccess: () => confirmHandler() },
    );

  const formErrors = validate<ExpenseFormData>(formData, validationMapper);

  const expenseValue = useMemo(() => {
    const ui = toUiExpense(formData);
    ui.type = DomainExpenseType.ExpenseChildSupport;
    return ui;
  }, [formData]);

  const fieldErrors = useMemo(
    () => expenseErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyExpenseChange = (patch: Partial<typeof expenseValue>) => {
    const mapped = fromUiExpensePatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      updateField(key as keyof ExpenseFormData)(val);
    }
  };

  return (
    <SlideUpModal
      title="Child Support"
      isOpen={true}
      closeHandler={closeHandler}
      hasDismissButton={true}
      actionButtonText={expense?.id ? "Save" : "Add expense"}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Expense Details"
        headerIcon={
          <MoneySecurityIcon
            variant="bulk-rounded"
            size={16}
            className="text-[#017C3F]"
          />
        }
      >
        <FormTableBody>
          <ExpenseFields
            expense={expenseValue}
            showStartDate
            showEndDate
            showOngoing
            showChildrenAges
            errors={fieldErrors}
            onFieldChange={(field, value) => {
              applyExpenseChange({ [field]: value });
            }}
            onBulkChange={applyExpenseChange}
          />
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
